import { NotificationService } from './../../common-services/notification.service';

import { Injectable } from '@angular/core';
import { Observable, map, of, switchMap, tap } from 'rxjs';
import { BaseApiService } from 'src/app/shared/services/base-api.service';
import { environment } from 'src/environments/environment';
import {
  GrpcLanguage,
  LoginInvestorInputDto,
  RefreshTokenInputDto,
  UserOTP,
  UserPassword,
  UserPhone,
  UserRegisterInput,
  ValidateCredantialsDto,
} from '../../data-models/identity.data.models';
import { HttpHeaders } from '@angular/common/http';
import {
  AddSubAccountDto,
  GetMyAccountsOutputDto,
  ProfileDataDto,
} from '../../data-models/user.data.models';
import { GeneralResponse } from '../../data-models/shared.data-models';
import { UserService } from '../../common-services/user.services';

@Injectable({ providedIn: 'root' })
export class UserDataService {
  private controllerUrl: string = `User/`;

  constructor(
    private baseApi: BaseApiService<any>,
    private notificationService: NotificationService,
    private userService: UserService,
  ) {}

  GenerateUserOTP(body: UserPhone): Observable<any> {
    return this.baseApi.Post(`${this.controllerUrl}GenerateUserOTP`, body).pipe(
      switchMap((response) => {
        const phoneNumber = body.phoneNumber || response?.data?.phoneNumber;

        if (!environment.production) {
          // Call GetUserDevOTP only in non-production environments
          return this.GetUserDevOTP({ phoneNumber }).pipe(
            tap((res) => {
              if (res && res?.data?.otp) {
                this.notificationService.success('OTP: ' + res.data.otp);
              }
            }),
            map(() => response), // Ensure consistent return
          );
        }
        return of(response);
      }),
    );
  }

  GetUserDevOTP(body: UserPhone): Observable<any> {
    return this.baseApi.Post(`${this.controllerUrl}GetUserDevOTP`, body);
  }
  ValidateUserOTP(
    oTP: string | undefined,
    phoneNumber: string | undefined,
    language: GrpcLanguage,
  ): Observable<any> {
    return this.baseApi.Get(
      `${
        this.controllerUrl
      }ValidateUserOTP?oTP=${oTP}&phoneNumber=${encodeURIComponent(
        phoneNumber ?? '',
      )}&language=${language}`,
    );
  }
  Login(body: LoginInvestorInputDto): Observable<any> {
    return this.baseApi.Post(`${this.controllerUrl}Login`, body);
  }
  SetNewPassword(body: UserPassword): Observable<any> {
    return this.baseApi.Post(`${this.controllerUrl}SetNewPassword`, body);
  }
  Register(body: UserRegisterInput): Observable<any> {
    return this.baseApi.Post(`${this.controllerUrl}Register`, body);
  }
  InvestorRegister(body: UserRegisterInput): Observable<any> {
    return this.baseApi.Post(`${this.controllerUrl}InvestorNewRegister`, body);
  }
  InvestorCompleteRegister(
    transactionId: string,
    otp: string,
    phoneNumber: string,
  ): Observable<any> {
    return this.baseApi.Post(`${this.controllerUrl}InvestorCompleteRegister`, {
      transactionId: transactionId,
      otp: otp,
      phoneNumber: phoneNumber,
    });
  }
  RefreshToken(body: RefreshTokenInputDto): Observable<any> {
    return this.baseApi.Post(`${this.controllerUrl}RefreshToken`, body);
  }
  Logout(): Observable<any> {
    return this.baseApi.Post(`${this.controllerUrl}Logout`, {});
  }
  completeRegistrationWithOTP(body: UserOTP): Observable<any> {
    return this.baseApi.Post(
      `${this.controllerUrl}CompleteRegistrationWithOTP`,
      body,
    );
  }
  SetUserProfileComplete(): Observable<any> {
    return this.baseApi.Post(`${this.controllerUrl}SetUserProfileComplete`, {});
  }
  ValidateCredentials(body: ValidateCredantialsDto): Observable<any> {
    return this.baseApi.Post(`${this.controllerUrl}ValidateCredentials`, body);
  }
  GetProfileData(): Observable<GeneralResponse<ProfileDataDto>> {
    return this.baseApi.Get(`${this.controllerUrl}GetProfileData`).pipe(
      tap((res) => {
        if (res && res.data && this.userService.currentUserCredantials) {
          const currentCreds = this.userService.currentUserCredantials;
          let hasChanges = false;
          const updatedCreds = { ...currentCreds };

          if (
            res.data.userKYCStatus &&
            updatedCreds.userKYCStatus !== res.data.userKYCStatus
          ) {
            updatedCreds.userKYCStatus = res.data.userKYCStatus;
            hasChanges = true;
          }

          if (
            res.data.moznUserStatus &&
            updatedCreds.moznUserStatus !== res.data.moznUserStatus
          ) {
            updatedCreds.moznUserStatus = res.data.moznUserStatus;
            hasChanges = true;
          }

          if (
            res.data.isCompleteProfile !== undefined &&
            updatedCreds.isCompleteProfile !== res.data.isCompleteProfile
          ) {
            updatedCreds.isCompleteProfile = res.data.isCompleteProfile;
            hasChanges = true;
          }

          if (hasChanges) {
            this.userService.currentUserCredantials = updatedCreds;
          }
        }
      }),
    );
  }
  RequestAddNotificationEmail(email: string): Observable<any> {
    return this.baseApi.Post(
      `${this.controllerUrl}RequestAddNotificationEmail`,
      { email: email },
    );
  }
  AddNotificationEmail(email: string, otp: string): Observable<any> {
    return this.baseApi.Post(`${this.controllerUrl}AddNotificationEmail`, {
      email: email,
      otp: otp,
    });
  }
  RequestChangePhoneNumber(phone: string): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.baseApi.Post(
      `${this.controllerUrl}RequestChangePhoneNumber`,
      JSON.stringify(`+966${phone}`),
      { headers: headers },
    );
  }
  ChangePhoneNumber(phone: string, otp: string): Observable<any> {
    return this.baseApi.Post(`${this.controllerUrl}ChangePhoneNumber`, {
      phoneNumber: `+966${phone}`,
      otp: otp,
    });
  }
  ChangePassword(
    oldPassword: string,
    newPassword: string,
    confirmNewPassword: string,
  ): Observable<any> {
    return this.baseApi.Post(`${this.controllerUrl}ChangePassword`, {
      oldPassword: oldPassword,
      newPassword: newPassword,
      confirmNewPassword: confirmNewPassword,
    });
  }
  UpdateIdExpirationDate(body: any): Observable<any> {
    return this.baseApi.Post(
      `${this.controllerUrl}UpdateIdExpirationDate`,
      body,
    );
  }
  VerifyYakeenUser(nationalId: any): Observable<any> {
    return this.baseApi.Post(
      `${this.controllerUrl}VerifyYakeenUser?nationalId=${nationalId}`,
      {},
    );
  }
  /**
   * @deprecated Stop using this old API. Do not call User/ValidateInvitedUser with National ID and OTP.
   * Use InvitationRegistrationDataService instead.
   */
  validateInvitedUser(
    nationalId: string,
    otp: string,
  ): Observable<
    GeneralResponse<{ transactionId: string; phoneNumber: string }>
  > {
    return this.baseApi.Post(`${this.controllerUrl}ValidateInvitedUser`, {
      nationalId,
      otp,
    });
  }
  GetMyAccounts(): Observable<GeneralResponse<GetMyAccountsOutputDto>> {
    return this.baseApi.Get(`${this.controllerUrl}GetMyAccounts`);
  }
  AddSubAccount(body: AddSubAccountDto): Observable<GeneralResponse<string>> {
    return this.baseApi.Post(`${this.controllerUrl}AddSubAccount`, body);
  }
  SwitchAccount(userId: string): Observable<any> {
    return this.baseApi.Post(`${this.controllerUrl}SwitchAccount`, {
      userId: userId,
    });
  }
  DeleteSubAccount(userId: string): Observable<GeneralResponse<boolean>> {
    return this.baseApi.Post(`${this.controllerUrl}DeleteSubAccount`, {
      userId: userId,
    });
  }
}
